import { useCallback, useContext, useMemo } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { DataContext } from '../context/DataContext';
import { WHISPERBI_SECURITY_API } from '../env';

// Mismo patrón exacto que SecurityRolesService.js/SecurityPoliciesService.js
// -- un Grupo de Usuario es una colección de usuarios con Roles y/o
// Políticas adjuntas directamente (ver functions/security-roles/app.py::
// create_group/update_group). No hay grupos de sistema en V1.
const API_BASE_URL = WHISPERBI_SECURITY_API;
const GROUPS_URL = `${API_BASE_URL}/groups`;

async function getIdToken() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    return tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('SecurityGroupsService.getIdToken: unable to fetch auth session', error);
    return null;
  }
}

async function request(url, options = {}, { requireAuth = true } = {}) {
  const init = { ...options };
  const headers = new Headers(init.headers || {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (requireAuth) {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No se encontró un token de autenticación para realizar la operación solicitada.');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  init.headers = headers;
  init.mode = init.mode || 'cors';
  init.cache = init.cache || 'no-cache';

  let response;
  try {
    response = await fetch(url, init);
  } catch (networkError) {
    console.error('SecurityGroupsService.request: network error', networkError);
    throw new Error('No se pudo conectar con el servicio de grupos. Verifica tu conexión o intenta más tarde.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const data = await response.json();
      const backendMessage = data?.message || data?.error || data?.errorMessage;
      if (backendMessage) {
        errorMessage = backendMessage;
      }
    } catch {
      const text = await response.text().catch(() => '');
      if (text) {
        errorMessage = text;
      }
    }
    throw new Error(errorMessage || 'Error inesperado al comunicar con el servicio de grupos.');
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

export async function listGroups() {
  return request(GROUPS_URL, { method: 'GET' });
}

export async function createGroup({ name, description, roleIds, policyIds } = {}) {
  if (!name || !name.trim()) {
    throw new Error('El nombre del grupo es obligatorio.');
  }

  const payload = {
    name: name.trim(),
    description: description || '',
    role_ids: Array.isArray(roleIds) ? roleIds : [],
    policy_ids: Array.isArray(policyIds) ? policyIds : []
  };

  return request(GROUPS_URL, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function updateGroup(groupId, { name, description, roleIds, policyIds } = {}) {
  if (!groupId) {
    throw new Error('Se requiere el identificador del grupo para actualizarlo.');
  }

  const payload = {
    name: name ? name.trim() : name,
    description,
    role_ids: Array.isArray(roleIds) ? roleIds : [],
    policy_ids: Array.isArray(policyIds) ? policyIds : []
  };

  const url = `${GROUPS_URL}/${encodeURIComponent(groupId)}`;
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
}

export async function deleteGroup(groupId) {
  if (!groupId) {
    throw new Error('Se requiere el identificador del grupo para eliminarlo.');
  }

  const url = `${GROUPS_URL}/${encodeURIComponent(groupId)}`;
  return request(url, { method: 'DELETE' });
}

function ensureGroupManagerAccess(isPlatformRoot, isTenantAdmin) {
  if (!isPlatformRoot && !isTenantAdmin) {
    throw new Error('Solo administradores de la cuenta pueden realizar esta operación.');
  }
}

export const useSecurityGroupsService = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const guard = useCallback(() => {
    ensureGroupManagerAccess(isPlatformRoot, isTenantAdmin);
  }, [isPlatformRoot, isTenantAdmin]);

  const guardedActions = useMemo(() => ({
    createGroup: async (payload) => {
      guard();
      return createGroup(payload);
    },
    updateGroup: async (groupId, payload) => {
      guard();
      return updateGroup(groupId, payload);
    },
    deleteGroup: async (groupId) => {
      guard();
      return deleteGroup(groupId);
    }
  }), [guard]);

  return {
    ...guardedActions,
    // Solo lectura: cualquier usuario autenticado puede consultarlos.
    listGroups
  };
};

const SecurityGroupsService = {
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  useSecurityGroupsService
};

export default SecurityGroupsService;
